import type Phaser from 'phaser';

/**
 * The journey through a portal, seen from the traveller's side (ADR 0017):
 * a wall of blue flames rises from the bottom of the screen until it fills
 * it, the jump happens behind it, then it rises on and away over the new
 * place. Purely visual: the server moved the player, the veil only hides
 * the cut.
 *
 * Drawn in the world, pinned to the camera's view every frame rather than
 * with a scroll factor of zero, so the camera zoom scales it like everything
 * else and every flame stays on whole pixels (CLAUDE.md rule 6).
 */

/** Sheet of the looping flame (`effects/teleport-flame/<i>.png`). */
export const TELEPORT_FLAME_SHEET = 'effect.teleport-flame';
const CLIP = 'teleport-veil';
/** The deep blue behind the arch's vortex, from the portal's own pixels. */
const DEEP = 0x0e1a39;
const RISE_MS = 520;
const CLEAR_MS = 620;
/**
 * The crest is packed tight, a flame every 12 px, so no gap shows at the
 * edge; the body behind it, already blue, only needs enough to flicker.
 */
const CREST_ROWS = 2;
const CREST_STEP = { x: 12, y: 16 };
const BODY_STEP = { x: 20, y: 30 };
const FLAME_W = 32;
const FLAME_H = 48;
/** Above the world, the name plates and the markers; the DOM panels stay over it. */
const DEPTH = 1_000_000;

export class TeleportVeil {
  private layer: Phaser.GameObjects.Container | undefined;
  /** Top edge of the veil, in pixels from the top of the view: the view's height when away. */
  private edge = 0;
  private tween: Phaser.Tweens.Tween | undefined;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly sheet: { atlas: string; prefix: string } | undefined,
  ) {}

  get shown(): boolean {
    return this.layer !== undefined;
  }

  /** Flames rise until the screen is blue; resolves once nothing of the world shows. */
  cover(): Promise<void> {
    const view = this.scene.cameras.main.worldView;
    this.build(view.width, view.height);
    this.edge = view.height;
    return new Promise((resolve) => {
      this.animate(-FLAME_H, RISE_MS, 'Quad.easeIn', 1, () => {
        resolve();
      });
    });
  }

  /** The flames rise on and fade, revealing where the traveller landed. */
  uncover(): void {
    if (this.layer === undefined) return;
    const height = this.scene.cameras.main.worldView.height;
    this.animate(-height * 2 - FLAME_H, CLEAR_MS, 'Quad.easeOut', 0, () => {
      this.destroy();
    });
  }

  /** Keep the veil over the camera's view; called every frame, after the camera moved. */
  update(): void {
    if (this.layer === undefined) return;
    const view = this.scene.cameras.main.worldView;
    this.layer.setPosition(Math.round(view.x), Math.round(view.y + this.edge));
  }

  destroy(): void {
    this.tween?.stop();
    this.tween = undefined;
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  /**
   * A block twice the view's height: a crest of flames on top, then deep
   * blue with flames all through it, so the veil reads as fire and not as a
   * curtain even while it holds.
   */
  private build(width: number, height: number): void {
    this.destroy();
    const scene = this.scene;
    const layer = scene.add.container(0, 0).setDepth(DEPTH);
    const depth = Math.ceil(height * 2);
    layer.add(
      scene.add.rectangle(0, FLAME_H / 2, Math.ceil(width) + FLAME_W, depth, DEEP).setOrigin(0, 0),
    );
    const clip = this.clip();
    for (let row = 0, y = 0; y < depth; row += 1) {
      const step = row < CREST_ROWS ? CREST_STEP : BODY_STEP;
      for (let x = -FLAME_W / 2; x < width + FLAME_W / 2; x += step.x) {
        // Rows staggered, heights jittered: a fire, not a wallpaper.
        const offset = row % 2 === 0 ? 0 : step.x / 2;
        const jitter = Math.floor(Math.random() * 6);
        const px = Math.round(x + offset - FLAME_W / 2);
        const py = y - jitter;
        if (clip !== undefined && this.sheet !== undefined) {
          const flame = scene.add
            .sprite(px, py, this.sheet.atlas)
            .setOrigin(0, 0)
            .play({
              key: clip,
              startFrame: Math.floor(Math.random() * 4),
            });
          layer.add(flame);
        } else {
          // No flame sprite packed: a plain blue tongue, still unmistakable.
          layer.add(
            scene.add.rectangle(px + 10, py + 16, 12, FLAME_H - 16, 0x14bcff).setOrigin(0, 0),
          );
        }
      }
      y += step.y;
    }
    this.layer = layer;
    this.update();
  }

  private clip(): string | undefined {
    if (this.sheet === undefined) return undefined;
    const { anims, textures } = this.scene;
    if (anims.exists(CLIP)) return CLIP;
    const texture = textures.get(this.sheet.atlas);
    const frames = texture
      .getFrameNames()
      .filter((name) => name.startsWith(`${this.sheet?.prefix ?? ''}/`))
      .sort(
        (a, b) => Number(a.slice(a.lastIndexOf('/') + 1)) - Number(b.slice(b.lastIndexOf('/') + 1)),
      );
    if (frames.length === 0) return undefined;
    anims.create({
      key: CLIP,
      frames: frames.map((frame) => ({ key: texture.key, frame })),
      frameRate: 10,
      repeat: -1,
    });
    return CLIP;
  }

  private animate(
    to: number,
    duration: number,
    ease: string,
    alpha: number,
    done: () => void,
  ): void {
    this.tween?.stop();
    const layer = this.layer;
    if (layer === undefined) {
      done();
      return;
    }
    const state = { edge: this.edge, alpha: layer.alpha };
    this.tween = this.scene.tweens.add({
      targets: state,
      edge: to,
      alpha,
      duration,
      ease,
      onUpdate: () => {
        // Whole pixels only: the veil moves like everything else on screen.
        this.edge = Math.round(state.edge);
        layer.setAlpha(state.alpha);
        this.update();
      },
      onComplete: done,
    });
  }
}
